import React, { useState } from "react";
import { cn, initials } from "../model/derive";
import { Modal, ModalButton } from "../Modal";

/** Ops 콘솔 공통 조각 — 시안(live-board-content · lead-inbox)의 칩 · 카드 · 기록 타임라인 */

/** 알약 칩 22px — tone: neutral · accent · warning · success · failed */
const PILL = {
  neutral:
    "bg-grayscale-gray01 text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]",
  gray: "bg-grayscale-gray01 text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]",
  accent: "bg-surface-green10 text-green-deep04",
  warning: "bg-functional-warning01 text-functional-warning04",
  success: "bg-functional-success01 text-functional-success04",
  failed: "bg-functional-failed01 text-functional-failed03",
};
export const Pill = ({ tone = "neutral", children, className }) => (
  <span
    className={cn(
      "inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-[9px] text-[12px] font-medium",
      PILL[tone] ?? PILL.neutral,
      className,
    )}
  >
    {children}
  </span>
);

/** 네모 회색 태그 20px — Question · Blocker · For reference · Ops only · Leader · Staff */
export const Tag = ({ children, title, className }) => (
  <span
    title={title}
    className={cn(
      "inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-md bg-grayscale-gray01 px-[7px] text-[11px] font-medium leading-none text-text-tertiary shadow-[inset_0_0_0_1px_#e7e5e1]",
      className,
    )}
  >
    {children}
  </span>
);

export const card =
  "rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4";
export const cardPad = "p-4 sm:px-6 sm:py-5";
export const listBox =
  "flex flex-col rounded-xl border border-green-deep03/[0.12]";

/** 카드 머리 — 제목 15/500 · 부제 13 · 오른쪽 */
export const CardHead = ({ title, sub, right }) => (
  <div className="flex items-baseline justify-between gap-3">
    <div className="flex flex-col gap-0.5">
      <h2 className="m-0 text-[15px] font-medium leading-[140%] text-text-primary">
        {title}
      </h2>
      {sub && (
        <span className="text-[13px] leading-[140%] text-text-secondary">
          {sub}
        </span>
      )}
    </div>
    {right && (
      <span className="shrink-0 whitespace-nowrap text-[13px] tabular-nums text-text-secondary">
        {right}
      </span>
    )}
  </div>
);

/** 섹션 머리 — h2 16/500 + 부제 */
export const SectionHead = ({ title, sub, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="flex flex-col gap-0.5">
      <h2 className="m-0 text-[16px] font-medium leading-[150%] text-text-primary">
        {title}
      </h2>
      {sub && (
        <span className="text-[13px] leading-[140%] text-text-secondary">
          {sub}
        </span>
      )}
    </div>
    {right}
  </div>
);

/** 둥근 아바타 — 이니셜 */
export const Avatar = ({ name, size = "h-8 w-8 text-[11px]", className }) => (
  <span
    className={cn(
      "flex shrink-0 items-center justify-center rounded-full bg-surface-green10 font-medium text-green-deep04",
      size,
      className,
    )}
  >
    {initials(name ?? "?")}
  </span>
);

/** 둥근 탭 묶음 (All · Comments · Ops) */
export const TabPills = ({ tabs, value, onChange }) => (
  <div
    role="tablist"
    className="flex gap-0.5 rounded-lg bg-green-deep03/[0.08] p-[3px]"
  >
    {tabs.map(([k, label]) => (
      <button
        key={k}
        type="button"
        role="tab"
        aria-selected={value === k}
        onClick={() => onChange(k)}
        className={cn(
          "rounded-md px-3 py-1.5 text-[13px] font-medium leading-[140%]",
          value === k
            ? "bg-grayscale-gray00 text-text-primary"
            : "text-text-secondary hover:text-text-primary",
        )}
      >
        {label}
      </button>
    ))}
  </div>
);

/**
 * 기록 타임라인 — key 는 카드(tint 면 연한 초록 · 태스크 상세의 운영진 행동), 아니면 한 줄. ops 면 점이 짙은 초록. opsOnly 면 "Ops only" 태그.
 * e: { n, key, title, who, time, detail, ops, tint, opsOnly }
 */
const LONG = 160;

/** 긴 기록(공지 본문 등)은 두 줄로 접고 "Read in full" 로 작은 모달을 띄운다 */
const Detail = ({ e, onOpen, className }) => {
  const long =
    (e.detail?.length ?? 0) > LONG || (e.detail ?? "").split("\n").length > 3;
  return (
    <span className={cn("flex min-w-0 flex-col items-start gap-1", className)}>
      <span
        className={cn(
          "min-w-0 whitespace-pre-line [overflow-wrap:anywhere] [text-wrap:pretty]",
          long && "line-clamp-2",
        )}
      >
        {e.detail}
      </span>
      {long && (
        <button
          type="button"
          onClick={() => onOpen(e)}
          className="text-[12px] font-medium text-green-deep03 hover:text-green-deep04"
        >
          Read in full
        </button>
      )}
    </span>
  );
};

export const LogList = ({ events, empty = "Nothing yet" }) => {
  const [open, setOpen] = useState(null);
  if (events.length === 0)
    return <p className="py-4 text-[14px] text-text-secondary">{empty}</p>;
  return (
    <>
      {open && (
        <Modal
          open
          title={open.title}
          subtitle={`${open.who}${open.time ? ` · ${open.time}` : ""}`}
          onClose={() => setOpen(null)}
          width="max-w-[480px]"
          sheetOnMobile
          footer={
            <ModalButton primary onClick={() => setOpen(null)}>
              Close
            </ModalButton>
          }
        >
          <p className="whitespace-pre-line text-[14px] leading-[160%] text-text-primary [overflow-wrap:anywhere] [text-wrap:pretty]">
            {open.detail}
          </p>
        </Modal>
      )}
      <ol className="flex flex-col py-1">
        {events.map((e) => (
          <li key={e.n} className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-3">
            <span className="relative flex justify-center">
              <span
                aria-hidden
                className="absolute inset-y-0 w-[1.5px] bg-green-deep03/[0.12]"
              />
              <span
                aria-hidden
                className={cn(
                  "relative rounded-full shadow-[0_0_0_3px_var(--bg-twodays,#fff)]",
                  e.key
                    ? cn(
                        "mt-3 h-2.5 w-2.5",
                        e.ops ? "bg-green-deep04" : "bg-green-deep02",
                      )
                    : "mt-2.5 h-1.5 w-1.5 bg-grayscale-gray04",
                )}
              />
            </span>
            {e.key ? (
              <div
                className={cn(
                  "mb-2 mt-1 flex min-w-0 flex-col gap-0.5 rounded-xl border px-3 py-2",
                  e.tint
                    ? "border-green-deep03/20 bg-surface-green10"
                    : "border-green-deep03/[0.12] bg-grayscale-gray00",
                )}
              >
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="min-w-0 text-[14px] font-medium leading-[140%] text-text-primary [overflow-wrap:anywhere]">
                    {e.title}
                  </span>
                  <span className="text-[12px] leading-[140%] text-text-secondary">
                    {e.who}
                    {e.time ? ` · ${e.time}` : ""}
                  </span>
                  {e.opsOnly && (
                    <Tag title="Participants don’t see this">Ops only</Tag>
                  )}
                </span>
                {e.detail && (
                  <Detail
                    e={e}
                    onOpen={setOpen}
                    className="text-[13px] leading-[150%] text-text-secondary"
                  />
                )}
              </div>
            ) : (
              <div className="flex min-w-0 flex-wrap items-baseline gap-1.5 pb-2 pt-1 text-[13px] leading-[150%]">
                <span className="text-text-primary">{e.who}</span>
                <span className="text-text-secondary">{e.title}</span>
                {e.opsOnly && (
                  <Tag title="Participants don’t see this">Ops only</Tag>
                )}
                {e.time && (
                  <span className="text-text-tertiary">· {e.time}</span>
                )}
                {e.detail && (
                  <Detail
                    e={e}
                    onOpen={setOpen}
                    className="basis-full text-text-tertiary"
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </>
  );
};

/** 시안 버튼 — primary(초록) · soft(8% 초록) · danger(빨간 테두리) · 꺼지면 5% + 흐린 글자 + 이유 툴팁 */
export const PillButton = ({
  variant = "soft",
  size = "m",
  disabled,
  tip,
  onClick,
  className,
  children,
  ...rest
}) => {
  const h =
    size === "l"
      ? "h-11 px-[18px] text-[14px]"
      : size === "s"
        ? "h-8 px-3.5 text-[13px]"
        : "h-10 px-[18px] text-[14px]";
  const look = disabled
    ? "bg-green-deep03/[0.05] text-text-disable cursor-not-allowed"
    : variant === "primary"
      ? "bg-green-deep02 text-grayscale-gray00 hover:bg-green-deep04"
      : variant === "danger"
        ? "border border-functional-failed03/30 bg-transparent text-functional-failed03 hover:bg-functional-failed01"
        : variant === "dangerFill"
          ? "bg-functional-failed03 text-grayscale-gray00 hover:bg-functional-failed04"
          : "bg-green-deep03/[0.08] text-text-primary hover:bg-green-deep03/[0.14]";
  return (
    <button
      type="button"
      disabled={disabled}
      title={disabled ? tip || undefined : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full font-medium",
        h,
        look,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
};

/** 시안 라디오 18px */
export const RadioDot = ({ on }) => (
  <span
    className={cn(
      "mt-px h-[18px] w-[18px] shrink-0 rounded-full",
      on
        ? "shadow-[inset_0_0_0_5px_#1dc37b]"
        : "shadow-[inset_0_0_0_1.5px_rgba(85,82,78,0.4)]",
    )}
  />
);
